import { useState } from 'react';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/core/lib/utils';

interface NoteTitleInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

/**
 * Document-style title input — plain `Input` from shadcn, no visible label.
 * `FloatingLabelInput` always renders a floating `<label>`, which doesn't fit the
 * Notion/Google-Docs-style title the Figma design calls for.
 *
 * @remarks
 * Focus state is tracked explicitly (not via `:focus-visible`) so the input-like
 * border only ever appears while actually editing, regardless of how focus was
 * acquired (click vs keyboard) — it reads as plain text otherwise.
 */
export function NoteTitleInput({ value, onChange, placeholder }: NoteTitleInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      placeholder={placeholder}
      aria-label={placeholder}
      maxLength={200}
      data-testid="notes-editor-title-input"
      className={cn(
        'mb-3 h-auto rounded-none border-0 border-b bg-transparent px-0 py-2 text-xl font-bold shadow-none outline-none ring-0 md:text-2xl',
        'focus-visible:ring-0 focus-visible:ring-offset-0',
        'placeholder:text-muted-foreground/50',
        isFocused ? 'border-ring' : 'border-transparent',
      )}
    />
  );
}
